import { useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { dateAfterDays } from "@buildpy/core/lib/format";
import { saveQuoteAsTemplate } from "@/api/actions/template";
import { defaultTemplateName, templateSaveGateOf } from "@buildpy/core/modules/template/from-quote";

interface Props {
  quoteId: string;
  title: string;
  /** 저장 안 한 편집이 있는가 — 템플릿은 저장본으로 만들므로 먼저 저장하게 한다. */
  dirty: boolean;
  /** 발송 등으로 잠겼는가 — 화면 편집은 저장할 길이 없으니 저장본으로 만들게 한다. */
  locked: boolean;
  // 넘기면 바깥(폰 더보기 메뉴)이 열고 닫는다 — 이때는 단추를 그리지 않는다.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // 바깥이 열 때 닫히면 포커스를 돌려줄 곳 — Trigger 가 없으면 Radix 는 body 로 보낸다.
  returnFocus?: () => void;
}

// 대표만 보이는 「템플릿으로 저장」 — 이름을 받아 저장본으로 새 템플릿(기본 아님)을 만든다.
const SaveTemplateDialog = ({ quoteId, title, dirty, locked, open: openProp, onOpenChange, returnFocus }: Props) => {
  const gate = templateSaveGateOf(dirty, locked);
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => (controlled ? onOpenChange?.(next) : setOpenState(next));
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  // 열릴 때마다 이름을 새로 채운다 — 바깥에서 열면 onOpenChange 가 안 불리므로 렌더 중에 연 순간을 잡는다.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setName(defaultTemplateName(title, dateAfterDays(0)));
  }

  const submit = async () => {
    setPending(true);
    const result = await saveQuoteAsTemplate(quoteId, name).catch(() => ({
      ok: false as const,
      message: "저장하지 못했습니다 — 다시 시도하세요",
    }));
    setPending(false);
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    toast.success(result.message, { description: "설정 › 템플릿에서 고칠 수 있습니다" });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            템플릿으로 저장
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className="max-w-md"
        onCloseAutoFocus={(e) => {
          if (!returnFocus) return;
          e.preventDefault();
          returnFocus();
        }}>
        <DialogHeader>
          <DialogTitle>템플릿으로 저장</DialogTitle>
          <DialogDescription>
            저장된 견적의 공종·항목·단위·수량식으로 새 템플릿을 만듭니다. 단가·공간·메모는 빠집니다.
          </DialogDescription>
        </DialogHeader>

        {gate === "dirty" && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
            저장하지 않은 편집이 있습니다 — 템플릿은 저장본으로 만드니 먼저 저장하세요.
          </p>
        )}
        {gate === "locked" && (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground" role="status">
            잠긴 견적이라 저장본으로 만듭니다 — 화면 편집은 포함되지 않습니다.
          </p>
        )}

        <Field label="템플릿 이름">
          <Input
            inputSize="sm"
            maxLength={60}
            name="template-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <DialogFooter>
          <Button
            disabled={gate === "dirty" || pending || name.trim() === ""}
            size="sm"
            variant="primary"
            onClick={() => void submit()}>
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTemplateDialog;
